import type { Occasion, OccasionDefinition, OccasionSettings } from '@unipostudio/shared-types';

/**
 * Pure date maths for occasions (blueprint S20, M10, A11). Every date is a
 * local calendar day: constructed with `new Date(y, m - 1, d)` and formatted by
 * hand as YYYY-MM-DD. `toISOString` is never used, because it converts to UTC
 * and shifts the day for anyone east of Greenwich.
 */

const MS_PER_DAY = 86_400_000;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Format a Date as YYYY-MM-DD using its local calendar fields. */
export function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Parse YYYY-MM-DD into a local-midnight Date (no timezone shift). */
export function toLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Midnight of the same local day. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** A new Date `days` calendar days after (or before, when negative) `date`. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Western (Gregorian) Easter Sunday, anonymous algorithm. 2026 = 5 April. */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * The n-th `weekday` (0 = Sunday) of `month` (1-12) in `year`. Negative n
 * counts from the end of the month (-1 = last). Null when the month has no
 * such day (a fifth Monday that does not exist).
 */
export function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date | null {
  if (n === 0 || !Number.isInteger(n)) return null;
  if (n > 0) {
    const first = new Date(year, month - 1, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    const candidate = new Date(year, month - 1, 1 + offset + (n - 1) * 7);
    return candidate.getMonth() === month - 1 ? candidate : null;
  }
  const last = new Date(year, month, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  const candidate = new Date(year, month - 1, last.getDate() - offset + (n + 1) * 7);
  return candidate.getMonth() === month - 1 ? candidate : null;
}

/**
 * The day a definition lands on in `year`, as YYYY-MM-DD, or null when it
 * does not occur that year (a table without that year, a one-off elsewhere).
 */
export function resolveDate(def: OccasionDefinition, year: number, country?: string): string | null {
  // A country that keeps the day differently (India's Eid) wins for the years it lists.
  const own = country ? def.countryDates?.[country]?.[String(year)] : undefined;
  if (own) return own;
  const r = def.recurrence;
  switch (r.type) {
    case 'fixed': {
      const date = new Date(year, r.month - 1, r.day);
      return date.getMonth() === r.month - 1 ? formatLocalDate(date) : null;
    }
    case 'nth_weekday': {
      const date = nthWeekdayOfMonth(year, r.month, r.weekday, r.n);
      return date ? formatLocalDate(date) : null;
    }
    case 'easter_offset':
      return formatLocalDate(addDays(easterSunday(year), r.days));
    case 'table':
      return r.dates[String(year)] ?? null;
    case 'once':
      return r.date.startsWith(`${year}-`) ? r.date : null;
    default:
      return null;
  }
}

/** Whether a definition applies to a workspace's country, region, industry and enabled scopes. */
export function matchesSettings(def: OccasionDefinition, settings: OccasionSettings): boolean {
  if (!settings.enabledScopes.includes(def.scope)) return false;
  if (def.countries.length > 0 && !def.countries.includes(settings.country)) return false;
  if (def.excludeCountries?.includes(settings.country)) return false;
  if (def.regions && def.regions.length > 0) {
    if (!settings.region || !def.regions.includes(settings.region)) return false;
  }
  if (def.industries && def.industries.length > 0) {
    if (!settings.industry || !def.industries.includes(settings.industry)) return false;
  }
  return true;
}

function resolveOccasion(def: OccasionDefinition, year: number, todayKey: string, country: string): Occasion | null {
  const date = resolveDate(def, year, country);
  if (!date) return null;
  const start = toLocalDate(date);
  const duration = Math.max(1, Math.floor(def.durationDays ?? 1));
  const endDate = formatLocalDate(addDays(start, duration - 1));
  const lead = formatLocalDate(addDays(start, -Math.max(0, def.leadDays)));
  // Never suggest a day in the past: a run already under way (Navratri,
  // Ramadan) suggests today until its last day (PC-33); once it is over, the
  // occasion day is the floor.
  const suggestedDate = lead >= todayKey ? lead : todayKey <= endDate ? todayKey : date;
  return { ...def, date, endDate, suggestedDate };
}

function compareOccasions(a: Occasion, b: Occasion): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  return a.name.localeCompare(b.name, 'en');
}

/**
 * Every occasion that touches [from, to] (inclusive, by calendar day) for the
 * given settings. Multi-day occasions are included when any day overlaps.
 * Sorted by date, then name.
 */
export function occasionsInRange(
  defs: readonly OccasionDefinition[],
  from: Date,
  to: Date,
  settings: OccasionSettings,
  today: Date = new Date(),
): Occasion[] {
  const fromKey = formatLocalDate(from);
  const toKey = formatLocalDate(to);
  if (fromKey > toKey) return [];
  const todayKey = formatLocalDate(today);
  const out: Occasion[] = [];
  // Start one year early so a run that began in December still shows in January.
  for (let year = from.getFullYear() - 1; year <= to.getFullYear(); year += 1) {
    for (const def of defs) {
      if (!matchesSettings(def, settings)) continue;
      const occasion = resolveOccasion(def, year, todayKey, settings.country);
      if (!occasion) continue;
      if (occasion.date > toKey || occasion.endDate < fromKey) continue;
      out.push(occasion);
    }
  }
  return dropDuplicateImports(out).sort(compareOccasions);
}

const STOP_WORDS = new Set(['day', 'the', 'and', 'of', 'saint', 'holiday']);

function nameTokens(name: string): Set<string> {
  return new Set(
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]s\b/g, '')
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 4 && !STOP_WORDS.has(w)),
  );
}

/**
 * The imported public-holiday catalogue repeats some of what the curated
 * dataset already says better (King's Day, Christmas). An imported occasion
 * is dropped when a curated or custom one lands on the same day and is either
 * a public holiday itself or shares a word of its name.
 */
export function dropDuplicateImports(occasions: Occasion[]): Occasion[] {
  const own = occasions.filter((o) => o.source !== 'imported');
  if (own.length === occasions.length) return occasions;
  const byDate = new Map<string, Occasion[]>();
  for (const o of own) byDate.set(o.date, [...(byDate.get(o.date) ?? []), o]);
  return occasions.filter((o) => {
    if (o.source !== 'imported') return true;
    const same = byDate.get(o.date);
    if (!same) return true;
    const tokens = nameTokens(o.name);
    return !same.some((c) => c.kind === 'public_holiday' || [...nameTokens(c.name)].some((t) => tokens.has(t)));
  });
}

/** The next `limit` occasions within `horizonDays` of `today`, including ones running today. */
export function upcomingOccasions(
  defs: readonly OccasionDefinition[],
  settings: OccasionSettings,
  today: Date,
  limit: number,
  horizonDays = 90,
): Occasion[] {
  const start = startOfDay(today);
  return occasionsInRange(defs, start, addDays(start, horizonDays), settings, start).slice(0, Math.max(0, limit));
}

/** Whole calendar days from `today` to `date` (YYYY-MM-DD); negative when it has passed. */
export function daysUntil(date: string, today: Date = new Date()): number {
  return Math.round((toLocalDate(date).getTime() - startOfDay(today).getTime()) / MS_PER_DAY);
}
